export type Capabilities = {
  /** Required for SharedArrayBuffer, and therefore for every multithreaded WASM build. */
  readonly crossOriginIsolated: boolean;
  readonly sharedArrayBuffer: boolean;
  readonly webgl2: boolean;
  readonly webgpu: boolean;
  readonly webCodecs: boolean;
  readonly opfs: boolean;
  /** Synchronous OPFS handles; worker-only, and the fast path for large writes. */
  readonly opfsSyncAccess: boolean;
  readonly audioWorklet: boolean;
  readonly offscreenCanvas: boolean;
  readonly fileSystemAccess: boolean;
  readonly promptApi: boolean;
  readonly translatorApi: boolean;
  readonly hardwareConcurrency: number;
  readonly deviceMemoryGb: number | null;
  readonly touchPrimary: boolean;
};

function hasWebgl2(): boolean {
  if (typeof document === "undefined") return false;
  try {
    return document.createElement("canvas").getContext("webgl2") !== null;
  } catch {
    return false;
  }
}

function hasOpfsSyncAccess(): boolean {
  return (
    typeof FileSystemFileHandle !== "undefined" &&
    "createSyncAccessHandle" in FileSystemFileHandle.prototype
  );
}

function matchesMedia(query: string): boolean {
  return typeof matchMedia === "function" && matchMedia(query).matches;
}

export function detectCapabilities(): Capabilities {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const deviceMemory = (nav as { deviceMemory?: number } | undefined)?.deviceMemory;

  return {
    crossOriginIsolated: globalThis.crossOriginIsolated === true,
    sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
    webgl2: hasWebgl2(),
    webgpu: nav !== undefined && "gpu" in nav,
    webCodecs: "VideoEncoder" in globalThis && "VideoDecoder" in globalThis,
    opfs: typeof nav?.storage?.getDirectory === "function",
    opfsSyncAccess: hasOpfsSyncAccess(),
    audioWorklet: typeof AudioWorklet !== "undefined",
    offscreenCanvas: "OffscreenCanvas" in globalThis,
    fileSystemAccess: "showSaveFilePicker" in globalThis,
    promptApi: "LanguageModel" in globalThis,
    translatorApi: "Translator" in globalThis,
    hardwareConcurrency: nav?.hardwareConcurrency ?? 1,
    deviceMemoryGb: typeof deviceMemory === "number" ? deviceMemory : null,
    touchPrimary: matchesMedia("(pointer: coarse)"),
  };
}
